import { AnyRecord, StringRecord } from "~/types/record";
import Head from "next/head";
import CancelJobModal from "~/shared/CancelJobModal";
import { parseDate } from "~/utils/date";
import React, { useCallback, useEffect, useState } from "react";
import { ScheduleJob } from "~/types/jobs";
import api from "~/adapters/api";
import yup, { createErrorDict } from "~/utils/yup";
import { toast } from "react-toastify";
import Jobs from "~/services/jobs";
import Title from "~/shared/Title";
import Panel from "~/shared/Panel";
import { useScheduleState } from "~/components/BookingByTokenPage/useScheduleState";
import { Scheduling } from "~/components/BookingByTokenPage/Scheduling";
import { SideImage } from "~/components/BookingByTokenPage/SideImage";
import { OnCondition } from "~/components/BookingByTokenPage/OnCondition";
import { ChoiceActions } from "~/components/BookingByTokenPage/ChoiceActions";
import { getAddressFormatted } from "~/utils/address";
import JobsInSamaPlaceTable from "~/components/BookingByTokenPage/JobsInSamaPlaceTable";
import Success from "~/components/BusinessSchedulingPage/Success";

type BookingByTokenPageProps = {
    responseStatus: "ok" | "error";
    errorDescription?: string;
    token: string;
    fields?: AnyRecord;
    schedulingConfig?: {
        schedulingTimeCalculation: any;
        defaultSegment: any;
        contactIsUser: any;
        defaultSchedulingType: any;
        defaultBriefing: any;
        doNotShowInPlaceContactInfo: any;
        doNotShowCustomerCode: any;
    }
};

type UpdateFieldsFn = (state: Partial<ScheduleJob>, value: any) => Partial<ScheduleJob>;

const schema = yup.object({
    observation: yup.string().notRequired().nullable(),
    jobKeyAddress: yup.string().notRequired().nullable(),
    jobKeyAddressObs: yup.string().notRequired().nullable(),
    scheduleDate: yup.string().required(),
    period: yup.string().oneOf(["manha", "tarde", "horario_comercial", null]).notRequired().nullable(),
});

const hasFieldError = async (validation: any, field: string, value: any): Promise<boolean | string> => {
    try {
        const fieldSchema = yup.reach(validation, field);
        return fieldSchema
            .validate(value)
            .then(() => false)
            .catch((err: Error) => err.message);
    } catch (e) {
        return false;
    }
};

export const BookingByTokenPage = ({ responseStatus, errorDescription, token, fields: serverFields, schedulingConfig }: BookingByTokenPageProps) => {
    const {
        title,
        description,
        willReschedule,
        reschedule,
        markToReschedule,
        markAsCanceled,
        canCancel,
        canReschedule,
        isAfterJobDate,
    } = useScheduleState({
        responseStatus,
        errorDescription,
        address: serverFields?.address,
        status: serverFields?.status,
        scheduleDate: serverFields?.scheduleDate,
        period: serverFields?.period,
        organizationId: serverFields?.organizationId
    });

    const [submitting, setSubmitting] = useState(false);
    const [jobCreated, setJobCreated] = useState(false);
    const [cancelId, setCancelId] = useState(null);
    const [errors, setErrors] = useState<StringRecord>({});
    const [fields, setFields] = useState<Partial<ScheduleJob>>(serverFields);
    const [jobKeyInOtherPlace, setJobKeyInOtherPlace] = useState<"sim" |"nao">("nao")
    const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
    const [subJobs, setSubJobs] = useState([])

    const markToCancel = useCallback(() => {
        if (fields.id) {
            setCancelId(fields.id);
        }
    }, [fields?.id]);

    const handleChange = (name: string, value: any, options: { extra?: boolean, updateFn?: UpdateFieldsFn } = {}) => {
        setFields((f: Partial<ScheduleJob>) => {
            if (options.updateFn) {
                return options.updateFn(f, value);
            }

            return { ...f, [name]: value };
        });
    };

    const handleBlur = async (name: string, value: any) => {
        const hasError = await hasFieldError(schema, name, value);
        setErrors((e: StringRecord) => ({ ...e, [name]: typeof hasError === "string" ? hasError : "" }));
    };

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setSubmitting(true);
        schema
            .validate(fields, { abortEarly: false })
            .then(() => {
                console.log("validando dados");
                const data = schema.cast(fields);

                const keyAddress: string | null = jobKeyInOtherPlace === "sim" ? getAddressFormatted({
                    addressParts: {
                        address: fields.jobKeyAddress,
                        cityName: fields.jobKeyCity,
                        zipCode: fields.jobKeyZipCode,
                        complement: fields.jobKeyComplement,
                        neighborhood: fields.jobKeyNeighborhood,
                        number: fields.jobKeyNumber,
                        stateAbbr: fields.jobKeyUf
                    }}) : null;

                const scheduleDate = parseDate(data.scheduleDate, "yyyy-MM-dd HH:mm:ss");

                return api.jobs.scheduling.scheduleByToken({
                    token: token,
                    jobId: fields.id,
                    data: { ...data, scheduleDate, jobKeyAddress: keyAddress, jobsIdsToSchedule: selectedJobs },
                });
            })
            .then((data: AnyRecord) => {
                console.log("afterPost", data);
                setJobCreated(true);
            })
            .catch((err: any) => {
                if (err.name.includes("ValidationError")) {
                    const allErrors = createErrorDict(err);

                    setErrors({ ...allErrors, general: "O formulário possui erros. Corrija-os antes de criar o job." });
                    return;
                }

                if (err?.response?.status === 400 && err?.response?.data?.context?.errors) {
                    setErrors(err.response.data.context.errors);
                }

                toast.error(`Erro ao criar job: ${err?.response?.data?.msg ?? err.message}`);

                console.log(err);
                console.log(err?.response?.data);
            })
            .then(() => setSubmitting(false));
    };

    const cancelJob = async (data) => {

        const selectedJobsIds = new Set(selectedJobs)
        const noDuplicatedIds = Array.from(selectedJobsIds)
        return Jobs.cancelByToken(fields.id, token, data?.reasonId ?? null, data?.cancelObservation ?? null, noDuplicatedIds )
            .then(() => {
                markAsCanceled();
                setCancelId(null)
            })
            .catch((e) => {
                console.log(e);
                if (e?.response?.data?.error) {
                    toast.error(e.response.data.error);
                } else {
                    throw e;
                }
            });
    };

    useEffect(() => {
        const jobs = fields?.jobsInSamePlace.filter( job => selectedJobs.includes(job?.jobId))
            .map( j => ({jobId: j?.jobId, extra_metragem: j?.metrage}))

        setSubJobs(jobs)
    }, [selectedJobs, fields]);


    if (jobCreated) {
        // return <SucessBusiness disableReturnButton={() => {}} values={{ contato: "" }} showVisitSiteButton={false} />;
        return <Success content="Job agendado" hrefButton="/"/>
    }

    return (
        <>
            <Head>
                <title>Agendamento - Fotop Serviços</title>
            </Head>

            <CancelJobModal
                show={cancelId !== null}
                onCancel={cancelJob}
                closeFn={() => {
                    setCancelId(null);
                }}
            />

            <div className="container mt-5">
                <div className="row d-flex align-items-stretch">
                    <div className={reschedule ? "col-12" : "col-sm-8"}>
                        <Panel fullHeight>
                            <div className="d-flex flex-column" style={{ height: "100%" }}>
                                {/* <OrganizationLogo logoUrl={fields?.logoUrl} /> */}
                                <OnCondition condition={!willReschedule || responseStatus === "error"}>
                                    <>
                                        <Title level="1">{title}</Title>
                                        {description}

                                        <OnCondition condition={!!fields?.scheduleDate}>
                                            {
                                                !!fields?.jobsInSamePlace.length &&
                                                <JobsInSamaPlaceTable
                                                    jobsInSamePlace={fields?.jobsInSamePlace}
                                                    loading={submitting}
                                                    setSelectedJobs={setSelectedJobs}
                                                    selectedJobs={selectedJobs}
                                                    jobId={fields?.id}
                                                />
                                            }

                                            <ChoiceActions
                                                canOpen={canReschedule}
                                                canCancel={canCancel}
                                                markToReschedule={markToReschedule}
                                                markToCancel={markToCancel}
                                                isAfterJobDate={isAfterJobDate}
                                            />
                                        </OnCondition>
                                    </>
                                </OnCondition>



                                <OnCondition condition={willReschedule}>
                                    {
                                        !!fields?.jobsInSamePlace?.length &&
                                        <JobsInSamaPlaceTable
                                            jobsInSamePlace={fields?.jobsInSamePlace}
                                            loading={submitting}
                                            setSelectedJobs={setSelectedJobs}
                                            selectedJobs={selectedJobs}
                                            jobId={fields?.id}
                                        />
                                    }

                                    <Scheduling
                                        onSubmit={onSubmit}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        errors={errors}
                                        schedulingConfig={schedulingConfig}
                                        fields={{ ...fields, subJobs, bookingType: !!subJobs?.length ? "multiple": "single"}}
                                        token={token}
                                        submitting={submitting}
                                        setJobKeyInOtherPlace={setJobKeyInOtherPlace}
                                        jobKeyInOtherPlace={jobKeyInOtherPlace}
                                    />

                                </OnCondition>
                            </div>

                        </Panel>
                    </div>

                    <SideImage hide={reschedule} />
                </div>
            </div>
        </>
    );
};
